import { FC, useMemo } from 'react';
import { useParams, useRouter } from 'next/navigation';
import { LineItem } from 'shared/types/wishlist/LineItem';
import Typography from 'components/commercetools-ui/atoms/typography';
import WishlistButton from 'components/commercetools-ui/organisms/wishlist/components/wishlist-button';
import { CurrencyHelpers } from 'helpers/currencyHelpers';
import useClassNames from 'helpers/hooks/useClassNames';
import usePath from 'helpers/hooks/usePath';
import ProductVariant from './product-variant';
import { ProductDetailsProps } from '..';
import { ITEM_CODE, ITEM_NAME, PRODUCT_CODE } from 'helpers/constants/attributes';
import SidebarPDP from './sidebar';
import ShopOnline from '../../shop-online';
import { GiFlexibleLamp } from 'react-icons/gi';

type ProductInformationProps = Omit<ProductDetailsProps, 'onAddToCart'>;

const ProductInformation: FC<ProductInformationProps> = ({ product, variant, onChangeVariant, inModalVersion }) => {
  const router = useRouter();
  const { locale } = useParams();
  const { path } = usePath();
  const attributesToDisplay = ['global-variant-identifier', 'colour', 'finish', 'size'];
  const discountPercentage =
    variant.discountedPrice &&
    (((variant.price?.centAmount as number) - (variant.discountedPrice.centAmount as number)) /
      (variant.price?.centAmount as number)) *
      100;

  // const updateVariantSKU = (sku: string) => {
  //   router.replace(`/${path.split('/').filter(Boolean).slice(0, -1).join('/')}/${sku}`);
  // };

  const productToWishlistLineItem = useMemo<LineItem>(() => {
    return {
      lineItemId: product?.productId ?? '',
      productId: product?.productId,
      name: product?.name,
      count: 1,
      variant: variant,
      addedAt: new Date(),
      _url: product?._url,
    };
  }, [product, variant]);

  const titleClassName = useClassNames([' mb-12', { '2xl:text-42 font-semibold leading-[4rem]': !inModalVersion }]);
  const priceClassName = useClassNames(['block text-18 lg:text-16 2xl:text-20  text-[#333] mt-12 lg:mt-6 xl:mt-0 ']);
  const discountedPriceClassName = useClassNames([
    'block font-regular leading-loose text-accent-red',
    { '2xl:text-18': !inModalVersion },
  ]);
  const variantLabelAttribute = attributesToDisplay.find((attribute) => variant?.attributes?.[attribute]);  
  return (
    <div className="px-0 md:px-0 lg:px-16 xl:px-16 2xl:px-16">
      <div className="flex items-center md:pt-40 lg:gap-20 lg:pt-0  xl:pt-0 2xl:pt-0 3xl:pt-0">
        <div>
          <Typography as="h3" className="text-14 font-normal leading-5 text-[#333] lg:text-10 2xl:text-14">
            {variant?.attributes?.[ITEM_CODE]}
          </Typography>
          <div className="mt-12 lg:mt-0">
            <h1 className="text-32 font-semibold leading-[2.75rem] tracking-tight text-primary-black lg:flex lg:items-center lg:gap-24 lg:text-28 lg:tracking-normal 2xl:text-36 2xl:leading-[3.75rem]">
              {/* {typeof variant?.attributes?.[ITEM_NAME] === 'string' ? variant?.attributes?.[ITEM_NAME] : product?.name} */}

              {
                typeof variant?.attributes?.[ITEM_NAME] === 'string'
                  ? variant?.attributes?.[ITEM_NAME].toLowerCase().includes("turoa")
                    ? variant?.attributes?.[ITEM_NAME].replace(/turoa/gi, "Tūroa")
                    : variant?.attributes?.[ITEM_NAME]
                  : product?.name && product?.name.toLowerCase().includes("turoa")
                  ? product?.name.replace(/turoa/gi, "Tūroa")
                  : product?.name
              }


              {/* <WishlistButton className="relative inline ml-16 -mt-8 lg:mt-0 lg:ml-0" lineItem={productToWishlistLineItem} /> */}
            </h1>
          </div>
        </div>
      </div>
      {discountPercentage ? (
        <div className="flex flex-row justify-between">
          <div className="flex items-center gap-8">
            <Typography className={discountedPriceClassName}>
              {'$' + ((variant.price?.centAmount as number) / 100).toFixed(2)}
            </Typography>
            <Typography className={priceClassName}>
              {CurrencyHelpers.formatForCurrency(variant.price ?? 0, locale)}
            </Typography>
          </div>

          {
            <span className="mb-8 ml-8 mt-10 flex h-[25px] w-[45px] items-center justify-center bg-accent-red text-11 text-neutral-100">
              {Math.round(discountPercentage)}%
            </span>
          }
        </div>
      ) : (
            <Typography className={priceClassName}>
              {'RRP ' + '$' + ((variant.price?.centAmount as number) / 100).toFixed(2)}
            </Typography>
      )}

      {/* Added ShopOnline button in all product tile. This is only for Methven NZ and not applicable for Methven AU */}
      <div style={{display:'flex', alignItems:'center', marginBottom:'-10px' }}>
          <ShopOnline itemCode={variant?.attributes?.[ITEM_CODE]}/>
      </div>

      {variantLabelAttribute &&
        <ProductVariant
          key={variantLabelAttribute}
          className="relative my-14"
          variants={product?.variants}
          currentVariant={variant}
          attribute={variantLabelAttribute}
          inModalVersion={inModalVersion}
          onClick={onChangeVariant}
        />
        }

    </div>
  );
};

export default ProductInformation;
